/* eslint-env browser */
/* global document */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import TetherComponent from 'react-tether';
import { clamp, debounce, compact, noop, isEqual } from 'lodash';
import classnames from 'classnames';

import { bemHelper, keyboardCode, ALL_CHARS } from '@dbk/ui-react-utils';

import DropdownOption from './dropdown-option';
import theme from './dropdown.theme';
import { PORTFOLIO_RELIDTYPE } from '../../../constants';

import {
  CLEAR_SEARCH_TIMER,
  normalizeOptions,
  isGroupRoot,
  ITEM_CHILD,
  PAGE_SIZE,
  DropdownPropTypes,
  DropdownDefaultProps,
  getSelectedText,
} from './dropdown-common';

import { MOBILE, IE, MACOS } from './dropdown.web.utils';

import styles from './dropdown.css';

const bem = bemHelper(styles, 'dropdown');

const {
  DOWN_ARROW,
  UP_ARROW,
  ENTER,
  SPACE,
  ESCAPE,
  TAB,
  PAGE_DOWN,
  PAGE_UP,
} = keyboardCode;

class DropdownList extends PureComponent {
  static propTypes = {
    ...DropdownPropTypes,
    id: PropTypes.string,
    className: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    tetherProps: PropTypes.object,
    type: PropTypes.oneOf(['dropdown', 'context-menu']),
    children: PropTypes.element.isRequired,
  };

  static defaultProps = {
    ...DropdownDefaultProps,
    id: undefined,
    className: undefined,
    onBlur: noop,
    onFocus: noop,
    tetherProps: undefined,
    type: 'dropdown',
    isPortfolio: false
  };

  static contextTypes = {
    inverse: PropTypes.bool,
  };

  static propOptions = {
    size: ['small', 'medium', 'large'],
    type: ['dropdown', 'context-menu'],
  };

  constructor(props) {
    super(props);
    const options = normalizeOptions(props);
    const index = this.getIndex(options, props);

    this.searchString = '';
    this.shouldRenderMobile = MOBILE && props.type === 'dropdown';

    this.state = {
      open: false,
      options,
      index,
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillReceiveProps(nextProps) {
    const sameProps = isEqual(this.props, nextProps);

    /* before calling the normalizeOptions and the rest
    of the logic we need to be sure that the props are changed */
    if (sameProps) {
      return;
    }

    const nextOptions = normalizeOptions(nextProps);
    const nextIndex = this.getIndex(nextOptions, nextProps);

    const { index, options } = this.state;
    const sameOptions = isEqual(options, nextOptions);

    if (!sameOptions) {
      this.setState({ options: nextOptions, index: nextIndex });
      return;
    }

    if (index !== nextIndex) {
      this.setState({ index: nextIndex });
    }
  }

  componentDidUpdate() {
    requestAnimationFrame(this.ensureSelectedVisible);
  }

  componentWillUnmount() {
    this.debounceClearSearch.cancel();
    document.removeEventListener('mousedown', this.handleClickOutside, false);
  }

  // eslint-disable-next-line complexity
  onKeyDown = (e) => {
    const code = e.keyCode || e.which;

    if (!ALL_CHARS.includes(code)) return;

    if (code !== TAB) {
      e.preventDefault();
    }

    const isOpen = this.hasIndex() && this.state.open;
    if (!isOpen) {
      if ([UP_ARROW, DOWN_ARROW, SPACE].includes(code)) {
        this.changeIndex(0, { open: true });
      }
    } else {
      if (code === UP_ARROW || code === DOWN_ARROW) {
        this.changeIndex(code === UP_ARROW ? -1 : 1);
        return;
      }

      if (code === ENTER || code === SPACE) {
        this.selectCurrent(e);
        return;
      }

      if (code === ESCAPE || code === TAB) {
        this.close();
        return;
      }

      if (code === PAGE_DOWN || code === PAGE_UP) {
        this.changeIndex(PAGE_SIZE * (code === PAGE_UP ? -1 : 1));
      }
    }
  };

  onKeyPress = (e) => {
    const code = e.keyCode || e.which;
    if (ALL_CHARS.includes(code)) return; // meta key pressed
    e.preventDefault();

    const key = e.key || String.fromCharCode(code);
    this.updateSearchString(key, e);
    this.debounceClearSearch();
  };

  getIndex(options, newProps) {
    const { index = null } = this.state || {};

    const { selected } = newProps;

    /* unselecting an option should change the index to zero */
    if (!selected || (typeof selected === 'string' && selected.trim() === ''))
      return 0;

    return selected ? options.findIndex((el) => el.value === selected) : index;
  }

  get optionsList() {
    const hasIndex = this.hasIndex();
    const { index } = this.state;
    const { renderOption, id, isPortfolio } = this.props;
    return this.state.options.map((option, i) => {
      const { value, tier } = option;

      const isHighlighted =
        (hasIndex && index === i) ||
        (!hasIndex && this.props.selected === value);
      const className = classnames(
        bem('item'),
        {
          [bem('item-selected')]: isHighlighted,
          [bem('item-portfolio')]: isPortfolio && option.relIdType === PORTFOLIO_RELIDTYPE.PORTFOLIOS,
        },
        bem('item', tier)
      );

      const events = {
        [IE ? 'onMouseDown' : 'onClick']: isGroupRoot(tier)
          ? null
          : (event) => {
              this.handleSelectItem(event, option);
            },
      };

      if (renderOption) {
        return renderOption(option, events, isHighlighted, className);
      }

      return (
        <DropdownOption
          item={option}
          cssClassNames={className}
          isHighlighted={isHighlighted}
          id={id}
          key={value}
          data-key={value}
          isItemRootGroup={isGroupRoot(tier)}
          onChange={this.handleSelectItem}
        />
      );
    });
  }

  getOptionsElement() {
    return this.shouldRenderMobile
      ? this.optionsElement
      : this.optionsElement &&
          this.optionsElement.childNodes &&
          this.optionsElement.childNodes[0];
  }

  getButtonWidth = () => {
    if (!this.childElement && !this.childElement.getBoundingClientRect)
      return null;
    const width = this.childElement.getBoundingClientRect().width;
    return `${width}px`;
  };

  handleOnClick = (event) => {
    const targetClassName = event.target.className.toString();
    if (!isGroupRoot(targetClassName)) this.toggleOpen();
  };

  close = () => this.setState({ open: false });
  open = () => this.setState({ open: true });

  toggleOpen = () => {
    if(this.props.onDropdownOpen){
      this.props.onDropdownOpen();
    }
    this.setState(({ open }) => ({ open: !open }));
  };

  optionsRef = (element) => {
    this.optionsElement = element;
  };

  childRef = (element) => {
    this.childElement = element;
  };

  isOptionSelectable(optionIndex) {
    const { options } = this.state;
    return !(options[optionIndex] && isGroupRoot(options[optionIndex].tier));
  }

  hasIndex() {
    return typeof this.state.index === 'number';
  }

  handleMobileSelectItem = (e) => {
    const { onChange } = this.props;
    const option = e.target;
    const optionList = e.target.options;
    const selectedOption = optionList[option.selectedIndex];

    const item = {
      value: selectedOption.getAttribute('value'),
      label: selectedOption.getAttribute('label'),
    };

    if (onChange) {
      onChange(e.target.value, item, e);
    }
  };

  handleSelectItem = (e, item) => {
    e.persist();
    this.toggleOpen();
    const { onChange } = this.props;

    if (onChange) {
      onChange(item.value, item, e);
    }
  };

  selectItemOfIndex(index, e) {
    const { onChange } = this.props;
    const { options } = this.state;

    if (onChange) {
      onChange(options[index].value, options[index], e);
    }
    this.close();
  }

  selectCurrent(e) {
    if (this.hasIndex()) {
      const { index } = this.state;
      this.selectItemOfIndex(index, e);
    }
  }

  // eslint-disable-next-line complexity
  ensureSelectedVisible = () => {
    const { open, index } = this.state;
    const optionsElement = this.getOptionsElement();

    if (!optionsElement || !open) {
      return;
    }

    const selectedElement = optionsElement.childNodes[index];

    if (!selectedElement) {
      return;
    }

    const elementPosition = selectedElement.getBoundingClientRect();
    const optionsPosition = optionsElement.getBoundingClientRect();

    let moveBy;

    if (elementPosition.top < optionsPosition.top) {
      moveBy = elementPosition.top - optionsPosition.top;
    }

    if (elementPosition.bottom > optionsPosition.bottom) {
      moveBy = elementPosition.bottom - optionsPosition.bottom;
    }

    if (moveBy) {
      optionsElement.scrollTop += moveBy;
    }
  };

  changeIndex(by, { open = false } = this.state) {
    const { options, index } = this.state;
    let newIndex = (index || 0) + by;

    const isNewUnselectable = !this.isOptionSelectable(newIndex);
    if (isNewUnselectable) {
      newIndex += Math.sign(by);
    }

    if (newIndex < 0 && isNewUnselectable) {
      const optionsElement = this.getOptionsElement();
      if (!optionsElement) return;
      optionsElement.scrollTop = 0;
      newIndex = index;
    }

    const firstSelectable = this.findSelectableIndex(options);
    const lastSelectable =
      options.length - 1 - this.findSelectableIndex([...options].reverse());
    this.setState({
      open,
      index: clamp(newIndex, firstSelectable, lastSelectable),
    });

    requestAnimationFrame(this.ensureSelectedVisible);
  }

  findSelectableIndex(options) {
    return options.findIndex((el) => !isGroupRoot(el.tier));
  }

  updateSearchString = (newChar, e) => {
    this.searchString += newChar.toLowerCase();

    const matchesSearchAndIsSelectable = ({ label }, i) =>
      this.isOptionSelectable(i) &&
      label.toLowerCase().indexOf(this.searchString) === 0;
    const firstOptionIndex = this.state.options.findIndex(
      matchesSearchAndIsSelectable
    );

    if (firstOptionIndex >= 0) {
      const { open } = this.state;
      if (!open) {
        this.selectItemOfIndex(firstOptionIndex, e);
      } else {
        this.setState({ index: firstOptionIndex });
      }
    }
  };

  debounceClearSearch = debounce(() => {
    this.searchString = '';
  }, CLEAR_SEARCH_TIMER);

  handleClickOutside = (evt) => {
    if (
      this.childElement &&
      !this.childElement.contains(evt.target) &&
      this.optionsElement &&
      !this.optionsElement.contains(evt.target)
    ) {
      if(this.props.onDropdownClose){
        this.props.onDropdownClose();
      }
      this.close();
    }
  };

  renderOptions() {
    const { size, className, fullWidth } = this.props;
    const { open } = this.state;

    const optionsWrapperClasses = classnames(
      bem(null, size),
      {
        [bem(null, 'open')]: open,
        [bem(null, 'inverse')]: this.context.inverse,
      },
      className
    );

    const dropdownOptionsClass = classnames(bem('options'), {
      [bem('options', 'full-width')]: fullWidth,
    });

    const responsiveWidthList = fullWidth
      ? {
          style: { width: `${this.getButtonWidth() || `auto`}` },
        }
      : {};

    return (
      <div {...responsiveWidthList} className={optionsWrapperClasses}>
        <div className={dropdownOptionsClass} ref={this.optionsRef}>
          <div
            className={`${bem('scroll')} ${MACOS ? bem('scroll', 'mac') : ''}`}
          >
            {this.optionsList}
          </div>
        </div>
      </div>
    );
  }

  renderMobile = () => {
    const { options } = this.state;
    const { selected } = this.props;
    return (
      <select onChange={this.handleMobileSelectItem} value={selected}>
        {options.map(({ value, label, tier, children }) => {
          if (isGroupRoot(tier)) {
            return (
              <optgroup label={label} key={value}>
                {children.map(this.renderMobileOption)}
              </optgroup>
            );
          }
          return tier !== ITEM_CHILD
            ? this.renderMobileOption({ value, label })
            : null;
        })}
      </select>
    );
  };

  renderMobileOption(option) {
    const { value, label } = option;
    return (
      <option value={value} label={label} key={value}>
        {label}
      </option>
    );
  }

  render() {
    const {
      children: childComponent,
      disabled,
      fullWidth,
      onBlur,
      onFocus,
      tetherProps,
      selected,
      defaultLabel,
      formatInputLabel,
      type,
    } = this.props;
    const { open, options } = this.state;

    const selectedTextValue = getSelectedText({
      options,
      selected,
      defaultLabel,
      formatInputLabel,
    });

    const tetherOffset = `${theme.tetherOffsetTop} ${theme.tetherOffsetLeft}`;

    const eventHandlers = Object.assign(
      {},
      ...compact([
        !disabled && {
          onBlur,
          onFocus,
        },
        !disabled &&
          !this.shouldRenderMobile && {
            onClick: this.handleOnClick,
            onKeyDown: this.onKeyDown,
            onKeyPress: this.onKeyPress,
          },
      ])
    );

    const childProps = {
      open,
      eventHandlers,
      childRef: this.childRef,
      ...(this.shouldRenderMobile
        ? { renderMobileOptions: this.renderMobile }
        : {}),
      ...(type === 'dropdown' ? { selectedTextValue } : {}),
    };

    if (this.shouldRenderMobile) {
      return React.cloneElement(childComponent, { ...childProps });
    }

    return (
      <TetherComponent
        constraints={[
          {
            to: 'window',
            attachment: 'together',
          },
          {
            to: 'scrollParent',
            attachment: 'together',
            pin: true,
          },
        ]}
        attachment="top left"
        targetAttachment="bottom left"
        classPrefix="dropdown__tether"
        /* to be able to override or extend tether options */
        {...tetherProps}
        classes={{
          element: classnames(bem('tether'), {
            [bem('tether', 'full-width')]: fullWidth,
          }),
        }}
        offset={tetherOffset}
      >
        {React.cloneElement(childComponent, { ...childProps })}
        {this.state.open && this.renderOptions()}
      </TetherComponent>
    );
  }
}

export default DropdownList;
